import { Box, Checkbox, Chip, FormControl, FormControlLabel, FormHelperText, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, SelectChangeEvent, Stack, Typography } from '@mui/material'
import { FunctionComponent, useContext, useEffect } from 'react'
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { IPostCustomField } from '../../../shared/services/custom-field/custom-field.model';
import CustomFieldService from '../../../shared/services/custom-field/custom-field.service';
import { UserDetailsContext } from '../../../context/user-details/user-details';
import { TooltipButton } from '../../presentation-components/miscellaneous-components/tooltip-button';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { ScreenLabelsContext } from '../../../context/screen-labels/screen-labels';

export const CustomFieldDialog: FunctionComponent<{
  errorMessage?: string,
  errorCode?: number,
  customFieldId?: string,
  open: boolean,
  closeHandler: () => void,
  isEdit: boolean,
  isSubmitting: boolean,
  onSubmit: (values: IPostCustomField, restForm: () => void, isEdit: boolean) => void,
}> = ({
  customFieldId,
  open,
  closeHandler,
  isEdit,
  onSubmit,
  isSubmitting,
  errorMessage,
  errorCode,
}) => {
    //Services
    const customFieldService = new CustomFieldService()
    //Context
    const screenLabelsContext = useContext(ScreenLabelsContext)
    const userDetailsContext = useContext(UserDetailsContext)
    const enabledProjects = userDetailsContext.userDetails?.organisation.enableProjects
    const enabledSubsidiaries = userDetailsContext.userDetails?.organisation.enableSubsidiaries
    //Variables
    const entryTypes = [
      'transaction',
      ...(enabledProjects ? ['project'] : []),
      'contactSupplier',
      'capitalGood',
      'vehicle',
      'location',
      ...(enabledSubsidiaries ? ['subsidiary'] : []),
    ]
    const compositeFilters = [
      screenLabelsContext.getLabel('location', { plural: false, capitalize: 'first' }),
      screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' }),
      screenLabelsContext.getLabel('project', { plural: false, capitalize: 'first' }),
      screenLabelsContext.getLabel('supplier', { plural: false, capitalize: 'first' }),
    ]
    // const delimiters = [
    //   "-", "_", "/", ":", ".", "|", "#", "~", "+"
    // ]
    //Use Effects
    useEffect(() => {
      if (open && customFieldId) {
        customFieldService.getOne(customFieldId)
          .then(response => {
            formik.setFieldValue('fieldName', response.fieldName)
            formik.setFieldValue('fieldType', response.fieldType)
            formik.setFieldValue('entryType', response.entryType)
            formik.setFieldValue('fieldStructure', response.fieldStructure)
            formik.setFieldValue('includeCompositeDelimiter', response.includeCompositeDelimiter)
            formik.setFieldValue('compositeDelimiter', response.compositeDelimiter)
          })
          .catch(err => console.log(err))
      }
    }, [open])

    const customFieldSchema = Yup.object().shape({
      fieldName: Yup.string().required('You must provide a field name'),
      fieldType: Yup.string().required('You must provide a field type'),
      entryType: Yup.string().required('You must provide a entry type'),
      fieldStructure: Yup.array()
        .of(Yup.string())
        .when('fieldType', {
          is: 'composite',
          then: schema =>
            schema
              .min(1, 'At least one component is required')
              .required('Structure of the field is required'),
          otherwise: schema => schema.notRequired(),
        }),
      includeCompositeDelimiter: Yup.boolean(),
      compositeDelimiter: Yup.string()
        .when('includeCompositeDelimiter', {
          is: true,
          then: schema =>
            schema
              .required('Composite delimiter is required'),
          // .oneOf(delimiters, 'Invalid delimiter selected'),
          otherwise: schema => schema.notRequired(),
        }),
    })

    const formik = useFormik<IPostCustomField>({
      initialValues: {
        fieldName: '',
        fieldType: '',
        entryType: 'transaction',
        fieldStructure: [],
        includeCompositeDelimiter: false,
        compositeDelimiter: '',
      },
      validationSchema: customFieldSchema,
      onSubmit: (values) => {
        if (values.compositeDelimiter === '') {
          values.compositeDelimiter = null
        }
        onSubmit(values, formik.resetForm, isEdit)
      }
    })

    const fieldTypeHandler = (e: SelectChangeEvent<string>) => {
      formik.setFieldValue('fieldType', e.target.value)
      if (e.target.value !== 'composite') {
        formik.setFieldValue('fieldStructure', '')
      }
    }

    const entryTypeHandler = (e: SelectChangeEvent<string>) => {
      formik.setFieldValue('entryType', e.target.value)
      if (formik.values.fieldType === 'composite' && e.target.value !== 'transaction') {
        formik.setFieldValue('fieldType', '')
        formik.setFieldValue('fieldStructure', '')
      }
    }

    const handleIncludeCompositeDelimiter = (e: React.ChangeEvent<HTMLInputElement>) => {
      formik.setFieldValue('includeCompositeDelimiter', e.target.checked)
      if (!e.target.checked) {
        formik.setFieldValue('compositeDelimiter', '');
      }
    }

    const cancelHandler = () => {
      formik.resetForm()
      closeHandler()
    }

    return (
      <DialogWrapper open={open} onClose={closeHandler} title={!isEdit ? 'Add Custom Field' : 'Edit Custom Field'} cancelHandler={cancelHandler} submitHandler={formik.submitForm} isSubmitting={isSubmitting}>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Entry Type</InputLabel>
          <Select
            id='entryType'
            name='entryType'
            label='Entry Type'
            value={formik.values.entryType}
            onChange={entryTypeHandler}
            error={formik.touched.entryType && Boolean(formik.errors.entryType)}
            sx={{ color: 'text.primary' }}>
            {entryTypes.map((entryType, index) => (
              <MenuItem key={index} value={entryType}>
                {entryType === 'transaction' ? 'Emissions' :
                  entryType === 'project' ? screenLabelsContext.getLabel('project', { plural: false, capitalize: 'first' }) :
                    entryType === 'contactSupplier' ? screenLabelsContext.getLabel('supplier', { plural: false, capitalize: 'first' }) :
                    entryType === 'capitalGood' ? screenLabelsContext.getLabel('product', { plural: false, capitalize: 'first' }) :
                    entryType === 'vehicle' ? screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' }) :
                    entryType === 'location' ? screenLabelsContext.getLabel('location', { plural: false, capitalize: 'first' }) :
                    entryType === 'subsidiary' ? screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' }) :
                      ''}
              </MenuItem>
            ))}
          </Select>
          {formik.touched.entryType && formik.errors.entryType &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
              {formik.errors.entryType ? <div>{formik.errors.entryType as string}</div> : null}
            </FormHelperText>
          }
        </FormControl>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Field Name</InputLabel>
          <OutlinedInput id='fieldName'
            name='fieldName'
            label='Field Name'
            fullWidth
            value={formik.values.fieldName}
            onChange={formik.handleChange}
            error={formik.touched.fieldName && Boolean(formik.errors.fieldName)} />
          {formik.touched.fieldName && formik.errors.fieldName &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
              {formik.errors.fieldName ? <div>{formik.errors.fieldName as string}</div> : null}
            </FormHelperText>
          }
        </FormControl>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Field Type</InputLabel>
          <Select
            id='fieldType'
            name='fieldType'
            label='Field Type'
            value={formik.values.fieldType}
            onChange={fieldTypeHandler}
            error={formik.touched.fieldType && Boolean(formik.errors.fieldType)}
            sx={{ color: 'text.primary' }}>
            <MenuItem value={'text'}>Text</MenuItem>
            <MenuItem value={'number'}>Number</MenuItem>
            {formik.values.entryType === 'transaction' && (
              <MenuItem value={'composite'}>Composite</MenuItem>
            )}
          </Select>
          {formik.touched.fieldType && formik.errors.fieldType &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
              {formik.errors.fieldType ? <div>{formik.errors.fieldType as string}</div> : null}
            </FormHelperText>
          }
          {errorMessage && errorCode === 1 &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
              {errorMessage}
            </FormHelperText>
          }
        </FormControl>
        {formik.values.fieldType === 'composite' &&
          <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
            <InputLabel htmlFor='fieldStructure'>Structure</InputLabel>
            <Select id='fieldStructure' name='fieldStructure' label='Structure'
              value={formik.values.fieldStructure}
              onChange={formik.handleChange}
              multiple
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value, index) => (
                    <Chip key={value} label={`${index + 1}. ${value}`} />
                  ))}
                </Box>
              )}
              error={formik.touched.fieldStructure && Boolean(formik.errors.fieldStructure)}
              input={<OutlinedInput label="Structure" />}>
              {compositeFilters.filter(f => !(f === 'Project' && !enabledProjects)).filter(f => !(f === 'Subsidiary' && !enabledSubsidiaries)).sort((a, b) => a.localeCompare(b)).map((filter, index) => (
                <MenuItem key={index} value={filter}>
                  <Checkbox checked={formik.values.fieldStructure?.includes(filter)} />
                  <ListItemText primary={filter} />
                </MenuItem>
              ))}
            </Select>
            <Stack direction='row' alignItems={'center'}>
              <Typography variant='subtitle1' sx={{ color: 'text.secondary', fontSize: '0.8rem', mt: 0.5, ml: 0.5 }}>Please specify the components in the correct order</Typography>
              {<TooltipButton
                text={'Select the components that make up your custom field; their reference values will be used to validate the input in the order you specify'}
                placement='top'
              />}
            </Stack>
            {formik.touched.fieldStructure && formik.errors.fieldStructure &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                {formik.errors.fieldStructure ? <div>{formik.errors.fieldStructure as string}</div> : null}
              </FormHelperText>
            }
          </FormControl>
        }
        {formik.values.fieldType === 'composite' &&
          <FormControl sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  id='includeCompositeDelimiter'
                  name='includeCompositeDelimiter'
                  checked={formik.values.includeCompositeDelimiter}
                  onChange={handleIncludeCompositeDelimiter}
                  sx={{ ml: 0.5 }}
                />}
              label={<Typography variant='body1'>Are the components seperated by a delimiter?</Typography>}
            />
          </FormControl>}
        {formik.values.includeCompositeDelimiter &&
          <FormControl fullWidth sx={{ mt: 1 }}>
            {/* <Stack direction='row' alignItems={'center'}> */}
            <InputLabel>Delimiter</InputLabel>
            {/* {<TooltipButton
                    text={`You must select from one of the following delimiters ${delimiters}`}
                    placement='top'
                  />}
                </Stack> */}
            <OutlinedInput
              id='compositeDelimiter'
              name='compositeDelimiter'
              label='Delimiter'
              fullWidth
              value={formik.values.compositeDelimiter}
              onChange={formik.handleChange}
              error={formik.touched.compositeDelimiter && Boolean(formik.errors.compositeDelimiter)}
              sx={{ color: 'text.secondary' }} />
            {formik.touched.compositeDelimiter && formik.errors.compositeDelimiter &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                {formik.errors.compositeDelimiter ? <div>{formik.errors.compositeDelimiter as string}</div> : null}
              </FormHelperText>
            }
          </FormControl>
        }
      </DialogWrapper>
    )
  }